import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
} from '@angular/core';
import { RefDataService } from '@app/providers/ref-data/ref-data.service';
import { map } from 'rxjs';
import * as _ from 'lodash';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  GoogleChartInterface,
  GoogleChartsLoaderService,
} from 'ng2-google-charts';
import { env } from 'process';
import { environment } from '@environments/environment';

@Component({
    selector: 'ea-map-chart',
    templateUrl: './ea-map-chart.component.html',
    styleUrls: ['./ea-map-chart.component.scss'],
    standalone: false
})
export class EaMapChartComponent implements OnInit, AfterViewInit {
  /**
   * @remark
   * this is a two dimensions Array, the first element is the header of the table.
   *
   * @example :
   * [['Country', 'Pourcentage'],
   * ['France', 10]]
   */
  @Input() set data(data) {
    this.mapReady = false;
    this._data = data || [];
    this.refreshMap();
  }
  get data() {
    return this._data;
  }
  _data;

  @Input() width: string = '100%';
  @Input() height: number = 256;
  @Input() region: any = 150;
  @Input() colorScale?: string[];
  @Input() set frenchTooltips(value: boolean) {
    if (value === this._frenchTooltips) return;
    this._frenchTooltips = value;
    if (this._data?.length) {
      this.mapReady = false;
      this.refreshMap();
    }
  }
  get frenchTooltips() {
    return this._frenchTooltips;
  }
  private _frenchTooltips = false;

  response: any[] = [];
  mapReady;
  private convertedTable: any[] | null = null;

  constructor(
    private el: ElementRef,
    private referenceDao: ReferenceDao,
    public loaderService: GoogleChartsLoaderService,
  ) {
    this.loaderService.load({
      mapsApiKey: environment.googleApiKey,
    });
  }

  geoChart: GoogleChartInterface = {
    chartType: 'GeoChart',
    dataTable: [
      ['Country', 'Pourcentage'],
      ['France', 25],
      ['Germany', 12],
      ['Netherlands', 13],
      ['Italy', 20],
      ['Spain', 20],
    ],
    options: {
      region: 150, // 150 Europe , 155 pour Europe de l'Ouest
      colorAxis: { colors: ['#B2C8F5', '#1C5CDE'] },
      backgroundColor: 'transparent',
      datalessRegionColor: '#ECF1FD',
      defaultColor: '#B2C8F5',
      // width: this.width,
      // height: +this.height,
      legend: 'none',
    },
  };
  darkMode: boolean = false;

  ngOnInit() {
    this.geoChart.options.region = this.region;
    if (this.colorScale && this.colorScale.length) {
      this.geoChart.options.colorAxis = { colors: this.colorScale };
      this.geoChart.options.defaultColor = this.colorScale[0];
    }
  }

  ngAfterViewInit() {
    window.onresize = this.changeMapSize.bind(this);
    const htmlElement = document.querySelector('html');
    this.detectDarkMode();
    const observer = new MutationObserver(() => {
      this.detectDarkMode();
    });
    observer.observe(htmlElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  detectDarkMode() {
    const htmlElement = document.querySelector('html');
    this.darkMode = htmlElement.classList.contains('theme-dark');
    this.mapReady = false;
    this.geoChart.options['datalessRegionColor'] = this.darkMode
      ? '#1D3755'
      : '#ECF1FD';
    this.addWhiteBorder();
    this.mapReady = true;
    if (this.geoChart.component) {
      this.geoChart.component.draw();
    }
  }
  addWhiteBorder(timeout?: number) {
    setTimeout(
      () => {
        const paths = this.el.nativeElement.getElementsByTagName('path');
        for (let i = 0; i < paths.length; i++) {
          paths[i].setAttribute('stroke', 'var(--panel-background-color)');
          paths[i].setAttribute('stroke-width', '2');
        }
      },
      timeout ? timeout : 500,
    );
  }
  refreshMap() {
    this.referenceDao.getCountries().subscribe(countries => {
      const parent = this.el.nativeElement.closest('.d-flex');
      let legend = null;
      let nextSibling = this.el.nativeElement.nextSibling;
      while (nextSibling) {
        if (
          nextSibling.classList &&
          nextSibling.classList.contains('ea-chart-legend')
        ) {
          legend = nextSibling;
          break;
        }
        nextSibling = nextSibling.nextSibling;
      }
      if (!legend) {
        this.geoChart.options['width'] = '100%';
      } else
        this.geoChart.options['width'] =
          parent.clientWidth - legend.clientWidth;
      const newData = this.groupFrenchCountry(this.data);
      this.geoChart.dataTable = newData;

      _(this.geoChart.dataTable).forEach((geoData, n) => {
        if (n > 0) {
          _.find(countries, ['value', geoData[0]]);
          for (let i = 0; i < countries.length; i++) {
            const element = countries[i];
            let value = countries[i].value;
            if (
              value
                .replace(/[^A-Z0-9]/gi, '_')
                .localeCompare(
                  geoData[0].replace(/[^A-Z0-9]/gi, '_'),
                  undefined,
                  { sensitivity: 'accent' },
                ) == 0
            ) {
              geoData[0] = this.frenchTooltips
                ? { v: countries[i].valueGb, f: geoData[0] }
                : countries[i].valueGb;
              break;
            }
          }
        }
      });
      this.convertedTable = this.geoChart.dataTable;
      setTimeout(() => (this.mapReady = true), 200);
    });
  }

  groupFrenchCountry(data) {
    let newData = [],
      frenchLabel = [
        'France',
        'Ile-de-France',
        'Province',
        'Paris',
        'Grand Paris',
      ];

    data.forEach(element => {
      if (frenchLabel.includes(element[0])) {
        let france = newData.find(e => e[0] == 'France');
        if (france) france[1] += element[1];
        else newData.push(['France', element[1]]);
      } else newData.push([...element]);
    });

    return newData;
  }

  changeMapSize() {
    this.mapReady = false;
    setTimeout(() => {
      const parent =
        this.el.nativeElement.closest('.d-flex') ||
        this.el.nativeElement.closest('.header-graphic');

      let legend = null;
      let nextSibling = this.el.nativeElement.nextSibling;
      while (nextSibling) {
        if (
          nextSibling.classList &&
          nextSibling.classList.contains('ea-chart-legend')
        ) {
          legend = nextSibling;
          break;
        }
        nextSibling = nextSibling.nextSibling;
      }
      if (this.geoChart) {
        this.geoChart.options['width'] =
          parent.clientWidth - (legend?.clientWidth || 0);
        this.geoChart.dataTable = this.convertedTable || this.data;
        this.mapReady = true;
        if (this.geoChart.component) this.geoChart.component.draw();
      }
    }, 0);

    if (this.darkMode) this.addWhiteBorder(0);
  }
}
